/** Browser preference only. Color values stay in index.css. */
import { create } from "zustand";

export type ThemePreference = "light" | "dark" | "system";
const STORAGE_KEY = "workbench:theme";
const SYSTEM_DARK = "(prefers-color-scheme: dark)";

function preferenceOf(value: string | null | undefined): ThemePreference {
  return value === "light" || value === "dark" ? value : "system";
}

function readPreference(): ThemePreference {
  try {
    return preferenceOf(localStorage.getItem(STORAGE_KEY));
  } catch {
    return "system";
  }
}

function applyTheme(preference: ThemePreference) {
  const dark = preference === "dark" || (preference === "system" && window.matchMedia?.(SYSTEM_DARK).matches);
  const theme = dark ? "dark" : "light";
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", theme === "dark" ? "#131416" : "#ffffff");
}

interface ThemeState {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
}

export const useThemeStore = create<ThemeState>((set) => ({
  preference: readPreference(),
  setPreference: (preference) => {
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // Switching still works when browser storage is unavailable.
    }
    applyTheme(preference);
    set({ preference });
  },
}));

/** Start once outside React; return cleanup for hot reload and tests. */
export function initializeTheme() {
  const preference = readPreference();
  useThemeStore.setState({ preference });
  applyTheme(preference);
  const media = window.matchMedia?.(SYSTEM_DARK);
  const onSystemChange = () => applyTheme(useThemeStore.getState().preference);
  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY && event.key !== null) return;
    const next = readPreference();
    useThemeStore.setState({ preference: next });
    applyTheme(next);
  };
  media?.addEventListener("change", onSystemChange);
  window.addEventListener("storage", onStorage);
  return () => {
    media?.removeEventListener("change", onSystemChange);
    window.removeEventListener("storage", onStorage);
  };
}
